/**
 * LIQUIDACIÓN · REPARTO DE CUENTAS ORIGEN Y ASIENTOS DE CUADRE (lógica pura, sin Angular).
 *
 * El tesorero reparte el total a pagar del lote (Bs) entre varias cuentas de la misma empresa pagadora, y el renglón
 * de cada pago puede llevar asientos (+ / −) con su motivo. Aquí vive solo la cuenta: el núcleo es quien manda.
 */

export interface FilaReparto { cuenta_origen: string; monto_bs: number | string | null; }
export interface Asiento { _id?: string; concepto?: string; signo: string; monto_bs: number | string; anulado?: any; }

const aNumero = (v: any): number => { const n = Number(String(v ?? '').replace(',', '.')); return isFinite(n) ? n : 0; };
/** A 2 decimales, sin errores de coma flotante (trabaja en céntimos). */
export const redondear = (n: any): number => Math.round((aNumero(n) + Number.EPSILON) * 100) / 100;
const centimos = (n: any): number => Math.round(redondear(n) * 100);

/** Total − lo repartido, al céntimo. Positivo = faltan; negativo = sobran. */
export const diferenciaBs = (total: any, filas: { monto_bs: any }[]): number =>
  (centimos(total) - (filas || []).reduce((s, f) => s + centimos(f.monto_bs), 0)) / 100;

/** Formato venezolano: 1.234.567,80 */
export const formatoBs = (n: any): string => {
  const [e, d] = Math.abs(redondear(n)).toFixed(2).split('.');
  return `${e.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${d}`;
};

export const estadoCuadre = (dif: number): { cuadra: boolean; clase: string; texto: string } => {
  const c = centimos(dif);
  if (c === 0) return { cuadra: true, clase: 'kb-badge-exito', texto: 'Cuadra ✓' };
  return c > 0 ? { cuadra: false, clase: 'kb-badge-alerta', texto: `Faltan Bs ${formatoBs(dif)}` }
    : { cuadra: false, clase: 'kb-badge-alerta', texto: `Sobran Bs ${formatoBs(dif)}` };
};

/** «Poner el resto aquí»: lo que falta para cuadrar, contando las demás filas (nunca menos de cero). */
export const ponerElResto = (total: any, filas: { monto_bs: any }[], indice: number): number => {
  const otras = (filas || []).filter((_, i) => i !== indice);
  return Math.max(0, diferenciaBs(total, otras));
};

export const validarReparto = (total: any, filas: FilaReparto[], motivo: string): { ok: boolean; razon: string } => {
  const f = filas || [];
  if (!f.length) return { ok: false, razon: 'Elige al menos una cuenta.' };
  if (f.some((x) => !x.cuenta_origen)) return { ok: false, razon: 'Elige la cuenta de cada fila.' };
  const codes = f.map((x) => x.cuenta_origen);
  if (new Set(codes).size !== codes.length) return { ok: false, razon: 'Hay una cuenta repetida: usa una sola fila por cuenta.' };
  if (f.some((x) => centimos(x.monto_bs) <= 0)) return { ok: false, razon: 'El monto de cada cuenta debe ser mayor que cero.' };
  const e = estadoCuadre(diferenciaBs(total, f));
  if (!e.cuadra) return { ok: false, razon: `El reparto no cuadra: ${e.texto}.` };
  if (!String(motivo || '').trim()) return { ok: false, razon: 'Escribe el motivo del reparto.' };
  return { ok: true, razon: '' };
};

/** Lo que se manda al núcleo (`PUT /lotes/:code/reparto`). */
export const cuerpoReparto = (filas: FilaReparto[], motivo: string): { cuentas: { cuenta_origen: string; monto_bs: number }[]; motivo: string } => ({
  cuentas: (filas || []).map((f) => ({ cuenta_origen: f.cuenta_origen, monto_bs: redondear(f.monto_bs) })),
  motivo: String(motivo || '').trim(),
});

/** El reparto se edita hasta que el lote se descarga; solo en lotes manuales. */
export const puedeEditarReparto = (lote: { state?: string; method?: string } | null): boolean =>
  !!lote && (!lote.method || lote.method === 'MANUAL') && ['BORRADOR', 'GENERADO', 'DESCARGADO'].includes(String(lote.state));

/** Los asientos de cuadre se agregan y anulan solo en borrador. */
export const puedeAsentar = (lote: { state?: string } | null): boolean => !!lote && lote.state === 'BORRADOR';

/** Cuentas ofrecidas: las de la misma empresa pagadora del lote (si el dato no viene, se ofrecen todas). */
export const cuentasParaReparto = (cuentas: any[], lote: { provider?: any } | null): any[] => {
  // /cuentas-origen trae la empresa poblada ({ _id, name }); el lote, su id.
  const idDe = (x: any): string => String(x && x._id != null ? x._id : x);
  const p = lote && lote.provider;
  return (cuentas || []).filter((c) => !p || !c.provider || idDe(c.provider) === idDe(p));
};

/** Bruto + asientos que suman − asientos que restan, sin contar los anulados. */
export const netoRenglon = (bruto: any, asientos?: Asiento[] | null): number => {
  const c = (asientos || []).filter((a) => !a.anulado).reduce((s, a) => s + (a.signo === '-' ? -1 : 1) * centimos(a.monto_bs), 0);
  return (centimos(bruto) + c) / 100;
};

/**
 * Cómo se ve el renglón (en vivo, 3-nov): generado, lo fijado por el núcleo; en BORRADOR el núcleo todavía no fija los Bs,
 * así que es una vista previa con la tasa del día del cierre (la misma del cuadre). Sin tasa, bruto y neto quedan null.
 */
export const vistaRenglon = (i: any, tasa: any): { bruto: number | null; asientos: number; neto: number | null; previa: boolean } => {
  const asientos = i.asientos_bs != null ? redondear(i.asientos_bs) : netoRenglon(0, i.asientos);
  if (i.amount_bs != null) return { bruto: redondear(i.amount_bs), asientos, neto: i.net_bs != null ? redondear(i.net_bs) : netoRenglon(i.amount_bs, i.asientos), previa: false };
  const t = aNumero(tasa);
  if (!(t > 0)) return { bruto: null, asientos, neto: null, previa: true };
  const bruto = redondear(aNumero(i.amount_usd) * redondear(t));
  return { bruto, asientos, neto: netoRenglon(bruto, i.asientos), previa: true };
};

/** La cuenta en un selector: banco, final y código (dos cuentas del mismo banco pueden terminar igual). */
export const etiquetaCuenta = (o: any): string => {
  const d = String((o && o.account_number) || '').replace(/\D/g, '');
  return [`${(o && o.bank_name) || 'Banco'} · ····${d.slice(-4)}`, o && o.code].filter(Boolean).join(' · ');
};

/** ASC-13 (minuta 3-nov, dudas entre punto y coma): el monto se teclea solo con dígitos y se ve como 1.234.567,80; el signo va en su selector. */
export const MASCARA_BS = { prefix: '', suffix: '', thousands: '.', decimal: ',', precision: 2, align: 'right', allowNegative: false };

export const validarAsiento = (f: any): { ok: boolean; errores: string[] } => {
  const e: string[] = [];
  if (!String(f?.concepto || '').trim()) e.push('Escribe el concepto.');
  if (f?.signo !== '+' && f?.signo !== '-') e.push('Elige si suma o resta.');
  if (centimos(f?.monto_bs) <= 0) e.push('El monto debe ser mayor que cero.');
  if (!String(f?.motivo || '').trim()) e.push('Escribe el motivo.');
  return { ok: !e.length, errores: e };
};

/** ASC-14/15 (5-nov): el asiento vigente que deja el renglón en Bs 0 y todavía no tiene la aprobación de la gerente de finanzas o del director. */
export const asientoPorAprobar = (a: any): boolean => !!a && !a.anulado && !!a.deja_en_cero && !(a.aprobacion && a.aprobacion.by);
/** ASC-19: el texto del renglón que se salda con el asiento, sin transferencia ni referencia bancaria. */
export const TEXTO_SALDADO = 'Saldado por compensación, sin transferencia';
export const esSaldado = (i: any): boolean => !!i && i.state === 'SALDADO_ASIENTO';

/** Selector de comercio del formulario de asientos: «comercio · cuenta por pagar», valor el payable_code. */
export const opcionesRenglones = (items: any[] | null | undefined): { valor: string; etiqueta: string }[] =>
  (items || []).map((i) => ({ valor: i.payable_code, etiqueta: [i.merchant_code, i.payable_code].filter(Boolean).join(' · ') }));

/** Con un solo renglón el comercio viene elegido; con varios, el usuario decide. */
export const renglonPreseleccionado = (opciones: { valor: string }[]): string => (opciones.length === 1 ? opciones[0].valor : '');

/** Todos los asientos de los renglones cargados (anulados incluidos), cada uno con su comercio y cuenta por pagar. */
export const asientosDelLote = (items: any[] | null | undefined): any[] =>
  (items || []).reduce((l: any[], i) => l.concat((i.asientos || []).map((a: any) => ({ ...a, merchant_code: i.merchant_code, payable_code: i.payable_code }))), []);

/** Cuadre compacto: los asientos vigentes se ven siempre; los anulados, solo bajo «Ver anulados». */
export const separarAsientos = (lista: any[] | null | undefined): { vigentes: any[]; anulados: any[] } => {
  const l = lista || [];
  return { vigentes: l.filter((a) => !a.anulado), anulados: l.filter((a) => !!a.anulado) };
};

/** La meta pequeña del encabezado del cuadre: la tasa BCV del día del cierre (borrador; LOT-03, Mario 3-nov) o la fijada al generar. */
export const metaTasa = (cuadre: { tasa?: any } | null | undefined, estado?: string): string => {
  const t = aNumero(cuadre && cuadre.tasa);
  if (String(estado) === 'BORRADOR') return t > 0 ? `Borrador · tasa BCV ${formatoBs(t)} (del cierre)` : 'Borrador · sin tasa BCV del cierre';
  return t > 0 ? `Tasa ${formatoBs(t)} fijada` : '';
};

/** Un monto del libro: «—» si todavía no hay con qué calcularlo (BORRADOR sin tasa). */
export const montoLibro = (n: any, hay = true): string => (!hay || n == null || n === '' ? '—' : formatoBs(n));

/** SEG-04 (Mario, 3-nov: «esto, debe cuadrar»): la suma de los asientos con su signo, para el cierre. */
export const textoAsientos = (n: any): string => `${aNumero(n) < 0 ? '−' : '+'}Bs ${formatoBs(Math.abs(aNumero(n)))}`;

/** SEG-04: el detalle de los asientos de un pago (al pasar el cursor): concepto y monto con signo. */
export const detalleAsientos = (ajuste: { asientos?: any[] } | null | undefined): string =>
  ((ajuste && ajuste.asientos) || []).map((a: any) => `${a.concepto} ${textoAsientos((a.signo === '-' ? -1 : 1) * aNumero(a.monto_bs))}`).join(' · ');
